import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/passwordInput';
import { Icon } from '@/components/common/Icon';
import { showSuccessNotification } from '@/utils/notifications';
import { useChangePasswordMutation } from '@/hooks/useAccountSecurity';

const MINIMUM_PASSWORD_LENGTH = 10;

export function SecuritySection() {
  const { t } = useTranslation('settings');
  const changePasswordMutation = useChangePasswordMutation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  async function submitPasswordChange(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
      setErrorMessage(t('practitioner.security.passwordHint', { count: MINIMUM_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage(t('practitioner.security.mismatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setErrorMessage(t('practitioner.security.unchanged'));
      return;
    }
    try {
      await changePasswordMutation.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSuccessNotification(t('practitioner.security.success'));
    } catch (changeError) {
      const backendMessage = resolveApiErrorMessage(changeError);
      setErrorMessage(
        backendMessage.length > 0 ? backendMessage : t('practitioner.security.genericError')
      );
    }
  }

  return (
    <Card padding="lg">
      <h2 className="text-lg font-bold text-zooindigo">{t('practitioner.security.heading')}</h2>
      <p className="mt-1 text-sm text-zoomuted">{t('practitioner.security.description')}</p>
      <form onSubmit={submitPasswordChange} className="mt-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="security-current-password">
            {t('practitioner.security.currentPassword')}
          </Label>
          <PasswordInput
            id="security-current-password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={function updateCurrentPassword(changeEvent) {
              setCurrentPassword(changeEvent.target.value);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="security-new-password">{t('practitioner.security.newPassword')}</Label>
          <PasswordInput
            id="security-new-password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={function updateNewPassword(changeEvent) {
              setNewPassword(changeEvent.target.value);
            }}
          />
          <p className="text-[11px] text-zoomuted">
            {t('practitioner.security.passwordHint', { count: MINIMUM_PASSWORD_LENGTH })}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="security-confirm-password">
            {t('practitioner.security.confirmPassword')}
          </Label>
          <PasswordInput
            id="security-confirm-password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={function updateConfirmPassword(changeEvent) {
              setConfirmPassword(changeEvent.target.value);
            }}
          />
        </div>

        {errorMessage.length > 0 ? (
          <div
            role="alert"
            className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
          >
            <Icon name="AlertCircle" />
            {errorMessage}
          </div>
        ) : null}

        <Button type="submit" isLoading={changePasswordMutation.isPending}>
          {t('practitioner.security.submit')}
        </Button>
      </form>
    </Card>
  );
}
